import { userZipCodeKey } from '../storage/storage-utils';
import { trackEvent } from '../analytics/tracking';
import { storeValue } from '../storage/storage-utils';
import { groupByStandardCategories } from '../products/product-utils';

import { RESET_CART, EMPTY_CART, RESTORE_CART, SET_DISPENSARY, SET_DISPENSARIES, SET_FEE_STRUCTURE, SET_DISPENSARY_PRODUCTS, SET_DELIVERY_ADDRESS, SET_ANON_USER_ZIP_CODE, ADD_TO_CART, REMOVE_ITEM, SUB_QUANTITY, ADD_QUANTITY, SET_DELIVERY_WINDOW, SET_PICKUP_TIME_SLOT, SET_IS_PICKUP, SET_ORDER_HISTORY, SET_BULK_DISCOUNT } from '../actions/action-types/cart-actions'

// Map key which allows a partial product map to be replaced with full product list
const INCOMPLETE_PRODUCT_MAP = "incomplete_product_map";

// tracking context defaults ( can be overridden by dispatches)
const defaultActionContext = {
  [RESET_CART] : 'logout',
  [EMPTY_CART] : 'place_order',
  [SET_DISPENSARY] : 'dispensary',
  [SET_FEE_STRUCTURE] : 'new_session',
  [SET_DISPENSARY_PRODUCTS] : 'dispensary',
  [SET_DISPENSARIES] : 'new_session',
  [SET_DELIVERY_ADDRESS] : 'dispensary', 
  [ADD_TO_CART] : 'dispensary', 
  [REMOVE_ITEM] : 'cart', 
  [SUB_QUANTITY] : 'cart',
  [ADD_QUANTITY] : 'cart'
};

// handle possibly missing data
const productFlowerGrams = (product) => {
  return product.flower_equivalent_grams || 0; 
}

// Restore Cart-related functions
// TODO: Need to handle products not loaded
const restoreAddedItems = (dispensaryId, items, state) => {
  const productsById = state.dispensaryProductsById.get(dispensaryId);
  if (productsById) {
    return items.map((item) => {
      const product = productsById.get(item.id);
      if (product) { 
        product.quantity = item.quantity;
      }
      return product;   
    });
  // Products not loaded
  } else {
    trackEvent('error_restore_cart');
    return [];
  }
}; 

const restoreTotal = (items) => {
  return items.reduce((total, item) => {
    return total + (item.quantity * (item.display_info.cost_usa_cents || 0)) 
  }, 0);
};

const restoreFlowerGrams = (items) => {
  return items.reduce((total, item) => {
    return total + (item.quantity * (item.flower_equivalent_grams || 0)) 
  }, 0);
};

const initState = {
  dispensaries: [],  // data for all dispensaries
  dispensaryMap: new Map(),
  dispensaryId: '',
  dispensaryProductsByCategory: {},
  dispensaryProductsById: new Map(),
  delivery_address: {},
  tel_number: '',
  user_name: '',
  anonymous_zip_code: '',
  addedItems:[],
  cannabis_tax_percent: 0, /* new */
  sales_tax_percent: 0,    /* new */ 
  delivery_fee_cents: 0, 
  delivery_service_fee_percent: 0,
  pickup_fee_cents: 0,
  pickup_service_fee_percent: 0,
  pickup_in_store_payment_fee_percent: 0,
  bulk_discount_cents: 0,
  bulk_discount_label: '',
  total: 0,
  flower_equivalent_grams: 0,
  delivery_window: {},
  pickup_time_slot: {},
  is_pickup: false,
  previous_orders: []  /* used to optimize dispensary listings */
};


const cartReducer= (state = initState, action) => {

  // first log action - ignore redux init
  if (action.type && action.type.indexOf('@@redux') === -1) {
    // action, type (context), id (product/dispensary)
    let context = defaultActionContext[action.type] || '';
    // default context is dispensary id 
    const { dispensaryId } = action.dispensaryId ? action : state;
    // default Id is product id (add to cart) or dispensary name (set address etc.)
    const Id = action.Id || dispensaryId;
    // track zip codes for certain actions
    const { zip_code } = action.delivery_address ? action.delivery_address : action; 
    if (zip_code && (action.type === SET_ANON_USER_ZIP_CODE || action.type === SET_DELIVERY_ADDRESS)) {
      context = zip_code; // or set as Id?
    }
    trackEvent( action.type, context, Id);
  }

  // empty cart and clear out any user-specific data
  if (action.type === RESET_CART){
    return {
      ...state,
      user_name: '',
      delivery_address: {},
      tel_number: '',
      anonymous_zip_code: '',
      addedItems: [],
      bulk_discount_cents: 0,
      bulk_discount_label: '',
      total: 0,
      flower_equivalent_grams: 0,
      previous_orders: []
    }
  }  

  // empty cart, remove items
  if (action.type === EMPTY_CART){
    return {
      ...state,
      addedItems: [],
      bulk_discount_cents: 0,
      bulk_discount_label: '',
      total: 0,
      flower_equivalent_grams: 0,
    }  
  }

  /**
   * Restore a logged-in user's cart from a prior session 
   * (fetch handled by UserProvider at session start)
   * 
   * Note: Restore is abandonded if user is viewing a different dispensary 
   */
  if (action.type === RESTORE_CART){
    const { dispensaryId, items, orderType } = action;
    if (!state.dispensaryId || state.dispensaryId === dispensaryId) { 
      const addedItems = restoreAddedItems(dispensaryId, items, state);
      return {
        ...state,
        dispensaryId: dispensaryId,
        addedItems: addedItems,
        is_pickup: orderType !== 'delivery',
        total: restoreTotal(addedItems),
        flower_equivalent_grams: restoreFlowerGrams(addedItems)
      }  
    } else {
      return { ...state }; 
    }
  } 

  // Set data for all dispensaries
  if (action.type === SET_DISPENSARIES){
    // non-active dispemsaries are included in the map
    const mapById = action.dispensaries.reduce((map, dispensary) => map.set(dispensary.id, dispensary), new Map());
    // but not in the id array (used for lists)
    const activeDispensaries = action.dispensaries.filter(dispensary => dispensary.is_accepting_orders);
    const activeIds = activeDispensaries.reduce((active, disp) => {
      if (disp.is_accepting_orders) {
        active.push(disp.id);
      }
      return active;
    }, []);  
    console.log(`active dispensaries: ${activeIds}`);
    return {
      ...state,
      dispensaries: activeDispensaries,
      dispensaryMap: mapById
    }     
  }

  // Set meta data total cost calcs
  if (action.type === SET_FEE_STRUCTURE){
    return {
      ...state,
      cannabis_tax_percent: action.cannabis_tax_percent, /* new */
      sales_tax_percent: action.sales_tax_percent,       /* new */
      delivery_fee_cents: action.delivery_fee_cents,
      delivery_service_fee_percent: action.delivery_service_fee_percent,
      pickup_fee_cents: action.pickup_fee_cents,
      pickup_service_fee_percent: action.pickup_service_fee_percent,
      pickup_in_store_payment_fee_percent: action.in_store_payment_fee_percent
    }     
  }
  
  // Set any bulk discount for the current order
  if (action.type === SET_BULK_DISCOUNT){
    return {
      ...state,
      bulk_discount_cents: action.discount_cents,
      bulk_discount_label: action.discount_label
    }     
  }

  // Set data for all dispensaries
  if (action.type === SET_DISPENSARY_PRODUCTS){
    console.log('SETTING DISPENSARY PRODUCTS');
    // Only load full product list once per dispensary
    const dispensaryProducts = state.dispensaryProductsByCategory[action.dispensaryId];
    if (dispensaryProducts &&
        dispensaryProducts.get &&  
        !dispensaryProducts.get(INCOMPLETE_PRODUCT_MAP)) {
      return {
        ...state
      }   
    } else { 
      const newProductsByCategory = { ...state.dispensaryProductsByCategory };
        newProductsByCategory[action.dispensaryId] = groupByStandardCategories(action.products); 
      
      const newProductsById = new Map(state.dispensaryProductsById);
        const byId = action.products.data.reduce((map, product) => map.set(product.id, product), new Map());
        newProductsById.set(action.dispensaryId, byId);
  
      return {
        ...state,
        dispensaryProductsByCategory: newProductsByCategory,
        dispensaryProductsById: newProductsById
      }     
    } 
  } 

  // Set selected dispensary, clear cart when switching dispensarires
  if (action.type === SET_DISPENSARY){
    const preserveCart = state.dispensaryId === action.dispensaryId;
    return {
      ...state,
      dispensaryId: action.dispensaryId,
      addedItems: preserveCart ? state.addedItems : [],
      total: preserveCart ? state.total : 0,
      delivery_window: preserveCart ? state.delivery_window: {},
      pickup_time_slot: preserveCart ? state.pickup_time_slot: {}
    }     
  }
  
  // Set delivery address --- street_address used for "Deliver to 123 Main Street"
  if (action.type === SET_DELIVERY_ADDRESS){
    // replace previously stored zip code response with account zip code
    if (action.delivery_address && action.delivery_address.zip_code) {
      storeValue(userZipCodeKey, action.delivery_address.zip_code);
    }
    return {
      ...state,
      delivery_address: action.delivery_address,
      tel_number: action.tel_number,
      user_name: action.user_name
    }     
  }

  // Set Zip Code from prompt for non-logged in users
  if (action.type === SET_ANON_USER_ZIP_CODE){
    return {
      ...state,  
      anonymous_zip_code: action.zip_code
    }
  }

  // Set the selected delivery window for use in placing orders
  if (action.type === SET_DELIVERY_WINDOW) {
    return {
      ...state,  
      delivery_window: action.delivery_window
    }
  }

  // Set the selected delivery window for use in placing orders
  if (action.type === SET_PICKUP_TIME_SLOT) {
    return {
      ...state,  
      pickup_time_slot: action.pickup_time_slot
    }
  }

  // Set selected delivery window for use in placing orders
  if (action.type === SET_IS_PICKUP) {
    return {
      ...state,  
      is_pickup: action.isPickup
    }
  }

  // Set order history to optimize dispensary listings
  if (action.type === SET_ORDER_HISTORY) {
    return {
      ...state,  
      order_history: action.orders
    }
  }

  // We'll generally need an item array clone to modify 
  const updatedItems = [ ...state.addedItems];
  // Add items to cart
  if (action.type === ADD_TO_CART){
    let addedItem;
    /**
     * We currently fetch and store all dispensary products on dispensary load.
     * To cart items from other pages (Your Orders) we support building the map as we add items to the cart 
     */
    let productsById = state.dispensaryProductsById.get(state.dispensaryId);
    // handle missing product map for dispensary
    if (!productsById) {
      productsById = new Map();
      if (action.productObj) {
        addedItem = action.productObj;
        productsById.set(INCOMPLETE_PRODUCT_MAP, {});
        productsById.set(action.Id, action.productObj);
        state.dispensaryProductsById.set(state.dispensaryId, productsById);
      } 
    // handle item missing from map ( full map not loaded )
    } else if (!productsById.get(action.Id) && action.productObj) {
      addedItem = action.productObj;
      productsById.set(action.Id, action.productObj);    
    // normal product retrieval
    } else { 
      // Clone product, we'll be adding custom attribs for bulk/tiered pricing
      addedItem = { ...productsById.get(action.Id) };
    }
    
    let newTotal = state.total;
    let newGrams = state.flower_equivalent_grams;

    if (addedItem) {
      // quantity is not guaranteed
      const quantityToAdd = action.quantity || 1; 
      
      // For add-to-cart, bulk quantity  
      newTotal += quantityToAdd * addedItem.display_info.cost_usa_cents;
      newGrams += productFlowerGrams(addedItem);

      // check if the items is already carted
      const previouslyCartedItem = updatedItems.find(item => action.Id === item.id);
      if (previouslyCartedItem) {
        previouslyCartedItem.quantity += quantityToAdd; 
      } else {
        addedItem.quantity = quantityToAdd;
        updatedItems.push(addedItem);
      }
    } else {
      trackEvent('error_add_to_cart', action.Id);
      console.log('UNABLE TO ADD ITEM, DISPENSARY PRODUCTS NOT LOADED');
    }  
    return {
      ...state,
      addedItems: updatedItems,
      total: newTotal,
      flower_equivalent_grams: newGrams
    } 
  }
  
  // Remove items from cart
  if (action.type === REMOVE_ITEM){
    const itemToRemove = updatedItems.find(item => action.Id === item.id);
    const itemsAfterRemoval = updatedItems.filter(item=> action.Id !== item.id);
    
    const itemTotal = itemToRemove.display_info.cost_usa_cents * itemToRemove.quantity; 
    const itemGrams = productFlowerGrams(itemToRemove) * itemToRemove.quantity;
    
    const newTotal = state.total - itemTotal
    const newGrams = state.flower_equivalent_grams - itemGrams;
    return {
      ...state,
      addedItems: itemsAfterRemoval,
      total: newTotal,
      flower_equivalent_grams: newGrams
    }
  }
  
  // Actions within cart
  if (action.type === ADD_QUANTITY) {
    const modifiedItem = updatedItems.find(item => item.id === action.Id);
    modifiedItem.quantity += 1; 
    const newTotal = state.total + modifiedItem.display_info.cost_usa_cents;
    const newGrams = state.flower_equivalent_grams + productFlowerGrams(modifiedItem);
    return {
      ...state,
      addedItems: updatedItems,
      total: newTotal,
      flower_equivalent_grams: newGrams
    }
  }

  if (action.type === SUB_QUANTITY) {  
    const modifiedItem = updatedItems.find(item => item.id === action.Id);
    const newTotal = state.total - modifiedItem.display_info.cost_usa_cents;  
    const newGrams = state.flower_equivalent_grams - productFlowerGrams(modifiedItem);
    // Remove if quantity is 0
    if (modifiedItem.quantity === 1){
      const itemsAfterRemoval = state.addedItems.filter(item => item.id !== action.Id);
      return {
        ...state,
        addedItems: itemsAfterRemoval,
        total: newTotal,
        flower_equivalent_grams: newGrams
      }
    } else {
      modifiedItem.quantity -= 1;
      return {
        ...state,
        addedItems: updatedItems,
        total: newTotal,
        flower_equivalent_grams: newGrams
      }
    }
  }
  
  // default
  return state;
}

export default cartReducer;
